import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Grid,
  FormControl,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import colors from '../../../Utils/colors';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheerioButton from '../../../Components/CheerioButton';
import images from '../../../Utils/images';
import axios from 'axios';
import { getAdsList, getAdAccountInsights, getMetaAdsMetrics } from '../../../Services';
import { useNavigate } from 'react-router-dom';
import SpinnerLoader from '../../../Components/SpinnerLoader';
import { toast } from 'react-toastify';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { eventsend } from '../../../Config/analyticsFunctions';
import dayjs from "dayjs";

const MetaAdsAnalytics = () => {
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState('');
  const [adStatus, setAdStatus] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState('25');
  const [currentPage, setCurrentPage] = useState(0);
  const [adsData, setAdsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [summary, setSummary] = useState({
    ctwa: 0,
    clicks: 0,
    leads: 0,
    ctr: 0,
    avgDailySpent: 0,
    avgTotalSpent: 0,
    impressions: 0,
  });
  const [timePeriod, setTimePeriod] = useState('last_30d');
  const [isMetaConnected, setIsMetaConnected] = useState(false);
  const [paginationCursors, setPaginationCursors] = useState({ before: "", after: "" });
  const timePeriodOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7d', label: 'Last 7 days' },
    { value: 'last_14d', label: 'Last 14 days' },
    { value: 'last_30d', label: 'Last 30 days' },
    { value: 'last_90d', label: 'Last 90 days' },
    { value: 'this_month', label: 'This month' },
    { value: 'this_year', label: 'This year' }
  ];
  const [isSyncing, setIsSyncing] = useState(false); // New state for sync button
  const [initialLoading, setInitialLoading] = useState(true);
  const localdata = useSelector((state) => state.main.localdata);
  const authtoken = useSelector((state) => state.main.auth_token);
  const [isChildAdAccount, setIsChildAdAccount] = useState(false);
  const [selectedAdAccountName, setSelectedAdAccountName] = useState('');
  useEffect(() => {
    setInitialLoading(true);
    fetchAdAccounts();
    // Other initialization...
  }, []);
  const MARKUP_RATE = 0.1;

  const fetchAdAccounts = async () => {
    try {
      setLoading(true);
      setIsSyncing(true);
      const token = authtoken;

      // Get ad account insights to get the list of accounts
      const accountInsightsResponse = await getAdAccountInsights({ before: "", after: "" }, token);

      if (accountInsightsResponse?.flag === true) {
        // Extract ad accounts from response
        setIsMetaConnected(true);
        const accounts = accountInsightsResponse?.data?.adaccounts?.data?.map(account => ({
          id: account?.id?.replace('act_', ''), // Remove 'act_' prefix if present
          name: account?.name || `Account ${account.id}`,
          fullId: account?.id, // Keep full ID for reference
          isChildAdAccount: account?.isChildAdAccount || false
        }));

        setAdAccounts(accounts);

        // First, try to find a child ad account
        const childAccount = accounts?.find(acc => acc?.isChildAdAccount === true);

        if (childAccount) {
          // Use the first child account found
          setSelectedAdAccount(childAccount?.id);
          setSelectedAdAccountName(childAccount.name);
          setIsChildAdAccount(true);
          fetchMetricsForAccount(childAccount?.id, timePeriod, true);
        } else {
          // If no child account exists, check if the default account from localdata exists
          const defaultAccountId = localdata?.metaConfig?.adAccountId;
          const defaultAccount = accounts?.find(acc => acc?.id === defaultAccountId || acc?.fullId === defaultAccountId);

          if (defaultAccount) {
            // Use the default account from localdata
            setSelectedAdAccount(defaultAccount?.id);
            setSelectedAdAccountName(defaultAccount.name);
            setIsChildAdAccount(defaultAccount?.isChildAdAccount || false);
            fetchMetricsForAccount(defaultAccount?.id, timePeriod, defaultAccount?.isChildAdAccount || false);
          } else if (accounts.length > 0) {
            // Use the first account if default is not found
            setSelectedAdAccount(accounts[0]?.id);
            setSelectedAdAccountName(accounts[0].name);
            setIsChildAdAccount(accounts[0]?.isChildAdAccount || false);
            fetchMetricsForAccount(accounts[0]?.id, timePeriod, accounts[0]?.isChildAdAccount || false);
          }
        }
      } else {
        toast.error(accountInsightsResponse?.message || "Error fetching ad accounts");
      }
    } catch (error) {
      toast.error('Error fetching ad accounts: ' + (error?.message || error));
    } finally {
      setLoading(false);
      setIsSyncing(false); // Re-enable sync button
      setInitialLoading(false);
    }
  };

  const syncSelectedAccount = async () => {
    if (!selectedAdAccount) {
      toast.error("Please select an ad account first");
      return;
    }

    try {
      setIsSyncing(true);
      setLoading(true);

      // Refresh metrics and ads for the selected account
      await fetchMetricsForAccount(selectedAdAccount, timePeriod, isChildAdAccount);

      toast.success("Account data synced successfully");
    } catch (error) {
      toast.error('Error syncing account data: ' + (error?.message || error));
    } finally {
      setIsSyncing(false);
      setLoading(false);
    }
  };

  // Function to fetch metrics for selected account
  const handleTimePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setTimePeriod(newPeriod);

    // Fetch metrics with the new time period
    if (selectedAdAccount) {
      fetchMetricsForAccount(selectedAdAccount, newPeriod, isChildAdAccount);
    }
  };

  const handleConnect = () => {
    navigate('/creator/discoverPlatforms/metaAdsManager');
  };

  // Update the fetchMetricsForAccount function to accept time period
  const fetchMetricsForAccount = async (accountId, period = timePeriod, childAdAccount = false) => {
    try {
      setLoading(true);
      const token = authtoken;

      // Call the getMetaAdsMetrics API with the selected account ID and time period
      const metricsResponse = await getMetaAdsMetrics(
        {
          adAccountId: `act_${accountId}`,
          dateLabel: period,
          isChildAdAccount: childAdAccount
        },
        token
      );

      if (metricsResponse && metricsResponse?.data) {
        updateSummaryFromMetrics(metricsResponse?.data, metricsResponse?.data?.ctwa);
      } else {
        toast.error("Failed to fetch ad metrics");
      }

      // Also fetch ads list for the selected account
      fetchAdsForAccount(accountId, childAdAccount);
    } catch (error) {
      toast.error('Error fetching metrics: ' + (error?.message || error));
      setLoading(false); // Ensure loading is set to false on error
      setInitialLoading(false);
    }
  };

  // Function to update summary from metrics data
  const updateSummaryFromMetrics = (data, ctwa = 0) => {
    console.log("updateSummaryFromMetrics", data);
    if (data && data?.length > 0) {
      const metrics = data[0];
      console.log("metrics", metrics);

      // Find lead value from actions array if it exists
      let leadValue = 0;
      let ctwaValue = 0;
      if (metrics.actions) {
        const leadAction = metrics.actions?.find(action => action?.action_type === 'lead');
        if (leadAction) {
          leadValue = parseInt(leadAction?.value) || 0;
        }
        // Find CTWA action - using onsite_conversion.messaging_first_reply
        const ctwaAction = metrics.actions?.find(action => action?.action_type === 'onsite_conversion.messaging_first_reply');
        if (ctwaAction) {
          ctwaValue = parseInt(ctwaAction?.value) || 0;
        }
      }
      // Update summary with the actual metrics data
      setSummary({
        ctwa: ctwaValue || 0,
        clicks: metrics?.clicks || 0,
        leads: leadValue || 0,
        ctr: metrics?.ctr ? parseFloat(metrics.ctr).toFixed(2) : "0.00",
        avgDailySpent: metrics.cpc ? parseFloat(metrics.cpc).toFixed(2) : "0.00",
        avgTotalSpent: metrics.spend ? parseFloat(metrics.spend).toFixed(2) : "0.00",
        impressions: metrics.impressions ? metrics.impressions : "0"
      });
    }
    if (data.length === 0) {
      setSummary({
        ctwa: 0,
        clicks: 0,
        leads: 0,
        ctr: 0,
        avgDailySpent: 0,
        avgTotalSpent: 0
      });
    }
  };

  // Function to fetch ads for the selected account
  const fetchAdsForAccount = async (accountId, childAdAccount = false) => {
    try {
      const token = authtoken;

      // Get ads list for the selected account
      const adsListResponse = await getAdsList({
        adAccountId: `act_${accountId}`,
        isChildAdAccount: childAdAccount,
        before: "",
        after: ""
      }, token);

      if (adsListResponse?.flag === true) {
        // Process the ads data
        const adsData = adsListResponse.data.data;

        const processedAds = adsData.map(ad => ({
          id: ad?.id,
          name: ad?.name,
          adset_id: ad?.adset_id,
          status: ad?.status,
          created_time: ad?.created_time,
          startDate: formatDate(ad?.created_time),
          stopDate: ad?.stop_time ? formatDate(ad?.stop_time) : "N/A",
        }));

        setAdsData(processedAds);

        // Store pagination cursors for future use
        if (adsListResponse?.data?.paging && adsListResponse?.data?.paging?.cursors) {
          setPaginationCursors(adsListResponse?.data?.paging?.cursors);
        }
      } else {
        toast.error(adsListResponse?.message || "Error fetching ads list");
      }
    } catch (error) {
      toast.error('Error fetching ads list: ' + (error?.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleAdAccountChange = (event) => {
    const newAccountId = event.target.value;
    setSelectedAdAccount(newAccountId);
    const selectedAccount = adAccounts?.find(acc => acc?.id === newAccountId);
    setSelectedAdAccountName(selectedAccount.name);
    const isChildAccount = selectedAccount?.isChildAdAccount === true;
    setIsChildAdAccount(isChildAccount);
    fetchMetricsForAccount(newAccountId, timePeriod, isChildAccount);
  };

  // Add this state to store pagination cursors

  // Function to fetch next page of ads
  // Function to fetch next page of ads
  const fetchNextPage = async () => {
    if (!paginationCursors?.after) return;
    try {
      setLoading(true);
      const token = authtoken;
      const adaccount = `act_${selectedAdAccount}`; // Use selectedAdAccount for the ad account ID
      const nextPageResponse = await getAdsList({
        adAccountId: adaccount,
        before: "",
        after: paginationCursors?.after
      }, token);



      if (nextPageResponse?.flag === true) {
        // Extract ads data from the response
        const adsData = nextPageResponse?.data?.data;

        // Process the ads data with insights
        const processedAds = adsData?.map(ad => {
          const adInsights = ad?.insights && ad?.insights?.data ? ad?.insights?.data[0] : {};

          return {
            id: ad?.id,
            name: ad?.name,
            adset_id: ad?.adset_id,
            status: ad?.status,
            created_time: ad?.created_time,
            startDate: formatDate(ad?.created_time),
            stopDate: ad?.stop_time ? formatDate(ad?.stop_time) : "N/A",
          };
        });

        setAdsData(processedAds);

        // Update pagination cursors for future navigation
        if (nextPageResponse?.data?.paging && nextPageResponse?.data?.paging?.cursors) {
          setPaginationCursors(nextPageResponse?.data?.paging?.cursors);
        } else {
          // If no more pages, clear the "after" cursor
          setPaginationCursors(prev => ({ ...prev, after: "" }));
        }

      } else {
        console.error('Invalid API response format:', nextPageResponse);
      }
    } catch (error) {
      console.error('Error fetching next page:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch previous page of ads
  const fetchPrevPage = async () => {
    if (!paginationCursors?.before) return;

    try {
      setLoading(true);
      const token = authtoken;
      const adaccount = `act_${selectedAdAccount}`;

      const prevPageResponse = await getAdsList({
        adAccountId: adaccount,
        before: paginationCursors?.before,
        after: ""
      }, token);

      if (prevPageResponse?.flag === true) {
        // Extract ads data from the response
        const adsData = prevPageResponse?.data?.data;

        // Process the ads data
        const processedAds = adsData?.map(ad => ({
          id: ad?.id,
          name: ad?.name,
          adset_id: ad?.adset_id,
          status: ad?.status,
          created_time: ad?.created_time,
          startDate: formatDate(ad?.created_time),
          stopDate: "N/A",
        }));

        setAdsData(processedAds);

        // Update pagination cursors for future navigation
        if (prevPageResponse?.data?.paging && prevPageResponse?.data?.paging?.cursors) {
          setPaginationCursors(prevPageResponse?.data?.paging?.cursors);
        } else {
          // If no more pages, clear the "before" cursor
          setPaginationCursors(prev => ({ ...prev, before: "" }));
        }

        console.log('Previous page loaded:', processedAds?.length);
      } else {
        console.error('Invalid API response format:', prevPageResponse);
      }
    } catch (error) {
      console.error('Error fetching previous page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (event) => {
    setAdStatus(event.target.value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(0); // Reset to first page when changing rows per page
  };
  // Helper function to format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Filter ads based on selected status
  const filteredAds = adStatus === ''
    ? adsData
    : adsData?.filter(ad => ad?.status?.toLowerCase() === adStatus?.toLowerCase());

  // Paginate the data
  const startIdx = currentPage * parseInt(rowsPerPage);
  const endIdx = startIdx + parseInt(rowsPerPage);
  const paginatedAds = filteredAds?.slice(startIdx, endIdx);

  // Get total pages
  const totalPages = Math.ceil(filteredAds?.length / parseInt(rowsPerPage));

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return '#e0f7e0'; // Light green
      case 'PAUSED':
        return '#f0e6ff'; // Light purple
      case 'REJECTED':
        return '#ffebee'; // Light red
      case 'STOPPED':
        return '#ffebee'; // Light red
      case 'LEARNING':
        return '#e0f7e0'; // Light green
      case 'PENDING REVIEW':
        return '#fff3e0'; // Light orange
      case 'IN REVIEW':
        return '#fff8e1'; // Light yellow
      default:
        return 'white';
    }
  };

  // Add this function to get the text color based on all possible statuses
  const getStatusTextColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return '#16A34A'; // Green
      case 'PAUSED':
        return '#9333EA'; // Purple
      case 'REJECTED':
        return '#DC2626'; // Red
      case 'STOPPED':
        return '#DC2626'; // Red
      case 'LEARNING':
        return '#16A34A'; // Green
      case 'PENDING REVIEW':
        return '#D97706'; // Orange
      case 'IN REVIEW':
        return '#D97706'; // Orange
      default:
        return 'black';
    }
  };


  const renderNotConnectedUI = () => {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
      }}>
        <img
          src={images.MetaTable}
          alt="Meta Ads Manager"
          style={{ marginBottom: '20px' }}
        />
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Meta Ads Manager
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
          Please setup your Ads Manager to get started
        </Typography>
        <CheerioButton
          borderStyle={{
            width: '15%',
            marginBlock: 8,
            borderColor: colors.primary03,
            paddingInline: 30,
            paddingBlock: 5,
            marginInlineEnd: 16,
            boderRadius: 10,
          }}
          textStyle={{ fontSize: 16, fontWeight: 600, color: colors.white }}
          btnText={'Connect'}
          backColor={colors.primary03}
          onclick={handleConnect}
          iconStyle={{ width: 16, height: 16 }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: '#f9f9f9', minHeight: '100vh', width: '100%' }}>
      {/* Header remains the same */}
      {initialLoading ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          <SpinnerLoader />
        </Box>
      )
        : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                backgroundColor: '#FFFFFF',
                px: 2,
                py: 1.5
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                Ads Manager
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 180, mr: 2 }}>
                  <Select
                    value={selectedAdAccount}
                    onChange={handleAdAccountChange}
                    size="small"
                    sx={{ borderRadius: '6px' }}
                  >
                    {adAccounts?.map(account => (
                      <MenuItem key={account?.id} value={account?.id}>
                        {account?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <img src={images.WalletIcon} style={{ paddingRight: 4, paddingBottom: 2 }} />
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    paddingRight: 20,
                  }}
                >
                  {localdata?.walletCurrency === 'INR' ? '₹' : '$'} {localdata?.metaWalletBalance?.toFixed(2)}
                </div>
                <CheerioButton
                  borderStyle={{
                    marginBlock: 8,
                    borderColor: colors.primary03,
                    paddingInline: 30,
                    paddingBlock: 5,
                    marginInlineEnd: 16,
                    boderRadius: 10,
                  }}
                  textStyle={{ fontSize: 16, fontWeight: 600, color: colors.white }}
                  btnText={'Create Ad'}
                  backColor={colors.primary03}
                  disabled={!isMetaConnected}
                  onclick={() => {
                    navigate(`/creator/metaAds/create/${selectedAdAccount}`, {
                      state: {
                        isChildAdAccount: isChildAdAccount,
                        adAccountName: selectedAdAccountName,
                      },
                    });
                    eventsend('Create_ad_clicked');
                  }}
                  icon={images.IPlusWhite}
                  iconStyle={{ width: 20, height: 20 }}
                  id="create-Ad-button"
                />
              </Box>
            </Box>

            {/* Summary section with dynamic data */}
            <Box sx={{ mb: 4, px: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="h6" fontWeight="bold">
                  Summary
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={timePeriod}
                    onChange={handleTimePeriodChange}
                    displayEmpty
                    sx={{
                      fontSize: '0.875rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                      },
                      borderRadius: '6px',
                      height: '36px'
                    }}
                    IconComponent={KeyboardArrowDownIcon}
                  >
                    {timePeriodOptions?.map(option => (
                      <MenuItem key={option?.value} value={option?.value}>
                        {option?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoOutlineIcon sx={{ fontSize: 16, color: '#000000', mr: 0.5, mb: 0.25, cursor: 'pointer' }} />
                <Typography variant="caption" sx={{ fontSize: 14, color: '#666666' }}>
                  Showing data as of: {dayjs().format('DD MMMM YYYY, hh:mmA')}
                </Typography>
                <Tooltip
                  title="The Meta expenditure and Wallet balance can fluctuate. The budget deduction cycle occurs daily at 11 PM."
                  arrow
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7C3AED',
                      ml: 1,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontWeight: 500
                    }}
                  >
                    Learn more
                  </Typography>
                </Tooltip>
              </Box>

              <Grid container spacing={3}>
                {/* <Grid item xs={3}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          No. of Clicks
                        </Typography>
                        <Tooltip title="Total number of clicks generated">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary.clicks}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid> */}

                <Grid item xs={4}>
                  <Card sx={{
                    borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                    boxShadow: 'none',
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Meta Spend
                        </Typography>
                        <Tooltip title="This is the total amount that has been spent on ads.">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        ₹{summary?.avgTotalSpent
                          ? isChildAdAccount
                            ? (summary.avgTotalSpent * (1 + MARKUP_RATE)).toFixed(2)
                            : parseFloat(summary.avgTotalSpent).toFixed(2)
                          : 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={4}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Avg Cost Per Result
                        </Typography>
                        <Tooltip title="Average cost of results(Meta Leads + CTWA Leads) generated">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary?.ctwa + summary?.leads > 0
                          ? `₹${isChildAdAccount
                            ? ((summary.avgTotalSpent * (1 + MARKUP_RATE)) / (summary.ctwa + summary.leads)).toFixed(2)
                            : (summary.avgTotalSpent / (summary.ctwa + summary.leads)).toFixed(2)
                          }`
                          : 0
                        }
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={4}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Cost Per Click
                        </Typography>
                        <Tooltip title="Cost per click per day">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        ₹{summary?.avgDailySpent
                          ? isChildAdAccount
                            ? (summary.avgDailySpent * (1 + MARKUP_RATE)).toFixed(2)
                            : parseFloat(summary.avgDailySpent).toFixed(2)
                          : 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>


              </Grid>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* <Grid item xs={3}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Impressions
                        </Typography>
                        <Tooltip title="Total number of clicks generated">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary.impressions}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid> */}

                <Grid item xs={3} >
                  <Card sx={{
                    borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                    boxShadow: 'none',
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total no. of results
                        </Typography>
                        <Tooltip title=" Total no. of results(Meta leads + CTWA leads) generated">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary?.leads + summary?.ctwa}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={3}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          No. of Meta Leads
                        </Typography>
                        <Tooltip title="Total number of FB leads generated">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary?.leads}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={3}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          No. of CTWA Leads
                        </Typography>
                        <Tooltip title="Total number of CTWA leads generated">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary?.ctwa}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={3}>
                  <Card
                    sx={{
                      borderRadius: 4, border: '1px solid #E5E7EB', // Tailwind's gray-200
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Click-through rate
                        </Typography>
                        <Tooltip title="Percentage of impressions that resulted in a click">
                          <InfoOutlineIcon
                            fontSize="small"
                            sx={{ ml: 0.5, fontSize: '16px', color: '#000000', cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                      <Typography variant="h3" fontWeight="bold">
                        {summary?.ctr} %
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Table section with dynamic data */}
            {isMetaConnected ? (<Grid sx={{ px: 2 }}>
              <Box sx={{ mb: 4, px: 1.5, borderRadius: 3, backgroundColor: '#FFFFFF' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, }}>
                  <Typography variant="h6" fontWeight="bold">
                    Click to WhatsApp Ads
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 180, mr: 1 }}>
                      <Select
                        value={adStatus}
                        onChange={handleStatusChange}
                        displayEmpty
                        renderValue={(selected) => {
                          if (selected === '') {
                            return 'Ad Status';
                          }
                          return selected;
                        }}
                        size="small"
                        sx={{ borderRadius: '6px' }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="paused">Paused</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="stopped">Stopped</MenuItem>
                        <MenuItem value="learning">Learning</MenuItem>
                        <MenuItem value="pending review">Pending Review</MenuItem>
                        <MenuItem value="in review">In Review</MenuItem>
                      </Select>
                    </FormControl>

                    <CheerioButton
                      borderStyle={{
                        marginBlock: 8,
                        borderColor: colors.primary03,
                        paddingInline: 30,
                        paddingBlock: 5,
                        marginInlineEnd: 16,
                        boderRadius: 10,
                      }}
                      textStyle={{ fontSize: 16, fontWeight: 600, color: colors.white }}
                      btnText={'Sync'}
                      backColor={colors.primary03}
                      onclick={syncSelectedAccount}
                      icon={images.refreshIcon}
                      iconStyle={{ width: 16, height: 16 }}
                      id="sync-button"
                      disabled={isSyncing || !selectedAdAccount}
                    />
                  </Box>
                </Box>

                {loading ? (
                  <>
                    <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100">
                      <SpinnerLoader />
                    </div>
                  </>
                ) : (
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px' }}>
                    <Table>
                      <TableHead sx={{ bgcolor: '#f9fafb' }}>
                        <TableRow>
                          <TableCell>Actions</TableCell>
                          <TableCell>Ad Name</TableCell>
                          <TableCell>Start Date</TableCell>
                          {/* <TableCell>Stop Date</TableCell> */}
                          <TableCell>Status</TableCell>
                          {/* <TableCell>Daily Budget</TableCell>
                  <TableCell>Reach</TableCell>
                  <TableCell>CPL</TableCell>
                  <TableCell>Leads</TableCell> */}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedAds?.length > 0 ? (
                          paginatedAds?.map((row) => (
                            <TableRow key={row?.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'center', cursor: 'pointer' }}>
                                  <IconButton onClick={() => {
                                    const adDetailData = {
                                      adName: row?.name,
                                      status: row?.status,
                                      selectedAdAccount: selectedAdAccount,
                                      // Financial metrics - using defaults since not in API
                                      totalBudgetUsed: "0",
                                      costPerClick: "0",
                                      dailyBudget: "0",
                                      // Dates
                                      startDate: row?.startDate,
                                      stopDate: row?.stopDate || "N/A",
                                      // Additional metadata
                                      createdAt: row?.startDate,
                                      fbPageName: 'Cheerio Business',
                                      isChildAdAccount: isChildAdAccount,
                                      adAccountName: selectedAdAccountName,
                                    };
                                    navigate(`analytics/${row?.id}`, { state: adDetailData });
                                    eventsend('Ad_analytics_clicked');
                                  }}>
                                    <img src={images.BarChartIcon} alt="Analytics" width={25} height={25} />
                                  </IconButton>
                                  {/* <IconButton>
                              <img src={images.CopyIcon} alt="Copy" width={30} height={30} />
                            </IconButton> */}
                                </Box>
                              </TableCell>
                              <TableCell>{row?.name}</TableCell>
                              <TableCell>{row?.startDate}</TableCell>
                              {/* <TableCell>{row.stopDate}</TableCell> */}
                              <TableCell>
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    bgcolor: getStatusColor(row?.status),
                                    color: getStatusTextColor(row?.status),
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {row?.status}
                                </Box>
                              </TableCell>
                              {/* <TableCell>₹{row.dailyBudget || "0"}</TableCell>
                      <TableCell>{row.reach || "0"}</TableCell>
                      <TableCell>₹{row.cpl || "0"}</TableCell>
                      <TableCell>{row.leads || "0"}</TableCell> */}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              No ads found matching your criteria
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    alignItems: 'center',
                    mt: 2,
                    px: 1,
                    pb: 2
                  }}
                >
                  {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Rows per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                displayEmpty
                sx={{ fontSize: '0.875rem' }}
              >
                <MenuItem value="10">10</MenuItem>
                <MenuItem value="20">20</MenuItem>
                <MenuItem value="50">50</MenuItem>
                <MenuItem value="100">100</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ ml: 2 }}>
              {startIdx + 1}-{Math.min(endIdx, filteredAds.length)} of {filteredAds.length}
            </Typography>
          </Box> */}

                  <Box
                    onClick={fetchPrevPage}
                    disabled={currentPage === 0}
                  >
                    <IconButton
                      size="small"

                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    Previous
                  </Box>
                  <Box
                    onClick={fetchNextPage}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                    <IconButton
                      size="small"
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Grid>) : (
              renderNotConnectedUI()
            )}
          </>
        )}
    </Box>
  );
};

export default MetaAdsAnalytics;
